import { Command } from "commander";
import chalk from "chalk";
import {
  getApiUrl,
  getActiveOrg,
  getToken,
  decodeZeroTokenPayload,
} from "../../lib/api/config";
import { listZeroConnectors } from "../../lib/api/domains/zero-connectors";
import {
  getZeroAgentUserConnectors,
  listZeroUserPermissionGrants,
} from "../../lib/api/domains/zero-agents";
import { getZeroOrg } from "../../lib/api/domains/zero-orgs";
import { withErrorHandler } from "../../lib/command/with-error-handler";
import { policyIcon } from "../../lib/utils/format-utils";
import {
  loadConnectorPermissionInfos,
  connectorPermissionGrantsToFirewallPolicies,
  type ConnectorPermissionInfo,
} from "./shared/firewall-permissions";

/**
 * Detect if running inside a zero sandbox (agent runtime).
 * Uses ZERO_AGENT_ID (not VM0_RUN_ID) because the zero CLI operates in the
 * zero agent context where ZERO_AGENT_ID is the canonical sandbox indicator.
 */
function isInsideSandbox(): boolean {
  return !!process.env.ZERO_AGENT_ID;
}

function formatConnectorIdentity(connector: {
  externalUsername: string | null;
  externalEmail: string | null;
  connectionStatus: "connected" | "reconnect-required";
}): string {
  let identity = "";
  if (connector.externalUsername && connector.externalEmail) {
    identity = `@${connector.externalUsername} (${connector.externalEmail})`;
  } else if (connector.externalUsername) {
    identity = `@${connector.externalUsername}`;
  } else if (connector.externalEmail) {
    identity = connector.externalEmail;
  }
  if (connector.connectionStatus === "reconnect-required") {
    identity += ` ${chalk.yellow("(needs reconnect)")}`;
  }
  return identity;
}

function printConnectorPermissions(info: ConnectorPermissionInfo): void {
  if (!info.hasPermissions) return;

  if (!info.hasPolicyEntry) {
    console.log(chalk.dim("    full access — no permission rules configured"));
    return;
  }

  if (
    info.permissions.length === 0 &&
    (!info.policies || Object.keys(info.policies).length === 0)
  ) {
    const unknownIcon = policyIcon(info.unknownPolicy);
    console.log(`    ${unknownIcon} unknown endpoints`);
    return;
  }

  const nameWidth = Math.max(
    "unknown endpoints".length,
    ...info.permissions.map((p) => {
      return p.name.length;
    }),
  );

  for (const perm of info.permissions) {
    const policy = info.policies?.[perm.name] ?? "deny";
    const icon = policyIcon(policy);
    const desc = perm.description ?? "";
    console.log(`    ${icon} ${perm.name.padEnd(nameWidth)}  ${desc}`);
  }

  const unknownIcon = policyIcon(info.unknownPolicy);
  console.log(
    `    ${unknownIcon} ${"unknown endpoints".padEnd(nameWidth)}  Endpoints not matching any rule`,
  );
}

/**
 * Workspace identity is supplementary: whoami must still print the agent
 * identity, capabilities, and connectors when the org lookup fails or 404s.
 */
async function printWorkspace(): Promise<void> {
  try {
    const org = await getZeroOrg();
    console.log(`Workspace:  ${org.name}`);
    if (org.tier) {
      console.log(`Tier:       ${org.tier}`);
    }
  } catch {
    // Silently skip — workspace info is supplementary
  }
}

async function showSandboxInfo(showPermissions: boolean): Promise<void> {
  const agentId = process.env.ZERO_AGENT_ID;
  const payload = decodeZeroTokenPayload();

  console.log(`Agent ID:   ${agentId}`);
  console.log(`Run ID:     ${payload?.runId ?? chalk.dim("unavailable")}`);
  console.log(`Org ID:     ${payload?.orgId ?? chalk.dim("unavailable")}`);
  await printWorkspace();

  // Capabilities section
  if (payload?.capabilities?.length) {
    console.log();
    console.log(chalk.bold("Capabilities:"));
    console.log(`  ${payload.capabilities.join(", ")}`);
  }

  // Connected Services section
  try {
    if (showPermissions) {
      // Full mode: fetch connector identities, current-user grants, and agent connector access.
      const [connectorsResult, grantsResult, enabledResult] =
        await Promise.allSettled([
          listZeroConnectors(),
          listZeroUserPermissionGrants(agentId!),
          getZeroAgentUserConnectors(agentId!),
        ]);

      if (connectorsResult.status === "rejected") return;

      const identities = connectorsResult.value.connectors.filter((c) => {
        return c.externalUsername !== null || c.externalEmail !== null;
      });

      if (identities.length === 0) return;

      let permissionInfoBySlug = new Map<string, ConnectorPermissionInfo>();
      const permissionDataAvailable =
        grantsResult.status === "fulfilled" &&
        enabledResult.status === "fulfilled";
      if (permissionDataAvailable) {
        const permissionInfos = await loadConnectorPermissionInfos({
          displayConnectorSlugs: identities.map((connector) => {
            return connector.slug;
          }),
          defaultPolicyConnectorSlugs: enabledResult.value,
          storedPolicies: connectorPermissionGrantsToFirewallPolicies(
            grantsResult.value,
          ),
        });
        permissionInfoBySlug = new Map(
          permissionInfos.map((info) => {
            return [info.connectorSlug, info];
          }),
        );
      }

      console.log();
      console.log(chalk.bold("Connectors:"));
      for (const connector of identities) {
        const identity = formatConnectorIdentity(connector);
        console.log(`  ${connector.slug.padEnd(14)}${identity}`);

        if (permissionDataAvailable) {
          const info = permissionInfoBySlug.get(connector.slug);
          if (info) {
            printConnectorPermissions(info);
          }
        }
      }
    } else {
      // Default mode: only fetch connector identities (1 API call)
      const connectors = await listZeroConnectors();
      const identities = connectors.connectors.filter((c) => {
        return c.externalUsername !== null || c.externalEmail !== null;
      });

      if (identities.length === 0) return;

      console.log();
      console.log(chalk.bold("Connectors:"));
      for (const connector of identities) {
        const identity = formatConnectorIdentity(connector);
        console.log(`  ${connector.slug.padEnd(14)}${identity}`);
      }
    }
  } catch {
    // Silently skip — connector info is supplementary
  }
}

async function showLocalInfo(): Promise<void> {
  const token = await getToken();
  const apiUrl = await getApiUrl();
  const payload = token ? decodeZeroTokenPayload(token) : undefined;
  const isExpired = payload ? payload.exp * 1000 <= Date.now() : false;
  const activeOrg = !isExpired ? await getActiveOrg() : undefined;

  // Auth section
  console.log(chalk.bold("Auth:"));
  if (!token) {
    console.log(`  Status:     ${chalk.dim("Not authenticated")}`);
  } else if (!payload) {
    console.log(`  Status:     ${chalk.red("Invalid ZERO_TOKEN")}`);
  } else if (isExpired) {
    console.log(`  Status:     ${chalk.red("Expired ZERO_TOKEN")}`);
  } else {
    console.log(
      `  Status:     ${chalk.green("Authenticated")} (via ZERO_TOKEN env var)`,
    );
  }
  console.log(`  API:        ${apiUrl}`);
  console.log();

  // Org section
  if (activeOrg) {
    console.log(chalk.bold("Org:"));
    console.log(`  Active:     ${activeOrg}`);
  }
}

export const zeroWhoamiCommand = new Command()
  .name("whoami")
  .description("Show agent identity, run ID, and capabilities")
  .option("--permissions", "Show full permission details for each connector")
  .addHelpText(
    "after",
    `
Examples:
  zero whoami
  zero whoami --permissions

Notes:
  - Inside sandbox: shows agent ID, run ID, org ID, and granted capabilities
  - Use --permissions to see detailed permission breakdown per connector
  - Your agent ID is also available as $ZERO_AGENT_ID`,
  )
  .action(
    withErrorHandler(async (options: { permissions?: boolean }) => {
      if (isInsideSandbox()) {
        await showSandboxInfo(options.permissions ?? false);
      } else {
        await showLocalInfo();
      }
    }),
  );
